import { format } from 'date-fns';
import { renderPassengerCode } from '@/feature/flight/search/lib/passenger-types';
import { TAirport, TFlightSearchFormData } from '@/feature/flight/search/types/search';

const formatDateTime = (date: Date): string => format(date, "yyyy-MM-dd'T'HH:mm:ss");

type TCabinValue = TFlightSearchFormData["cabin"];
type TFlightSegment = TFlightSearchFormData["flights"][number];

interface IPassengerTypeQuantity {
  Code: string;
  Quantity: number;
}

interface IOriginDestinationLeg {
  RPH: string;
  DepartureDateTime: string;
  OriginLocation: { LocationCode: string };
  DestinationLocation: { LocationCode: string };
  TPA_Extensions: {
    CabinPref: {
      PreferLevel: "Preferred";
      Cabin: TCabinValue;
    };
  };
}

interface IFlightSearchRequestPayload {
  JourneyType: string;
  PassengerTypeQuantity: IPassengerTypeQuantity[];
  OriginDestinationInformation: IOriginDestinationLeg[];
}

const JOURNEY_TYPE_MAP: Record<TFlightSearchFormData["tripType"], string> = {
  oneway: "1",
  roundtrip: "2",
  multicity: "3",
};

const getCabinPref = (cabin: TCabinValue) => ({
  PreferLevel: "Preferred" as const,
  Cabin: cabin,
});

const hasAirportCode = (airport?: TAirport): airport is TAirport =>
  Boolean(airport?.iata_code && airport.iata_code.trim().length > 0);

const isValidFlightSegment = (
  segment: TFlightSegment | undefined,
): segment is TFlightSegment & { departDate: Date } => {
  if (!segment || !(segment.departDate instanceof Date)) return false;
  if (!hasAirportCode(segment.from) || !hasAirportCode(segment.to)) return false;
  return segment.from.iata_code !== segment.to.iata_code;
};

const createLeg = (
  rph: number,
  segment: TFlightSegment & { departDate: Date },
  cabin: TCabinValue,
): IOriginDestinationLeg => ({
  RPH: String(rph),
  DepartureDateTime: formatDateTime(segment.departDate),
  OriginLocation: { LocationCode: segment.from.iata_code },
  DestinationLocation: { LocationCode: segment.to.iata_code },
  TPA_Extensions: { CabinPref: getCabinPref(cabin) },
});

const getJourneyFlights = (values: TFlightSearchFormData): TFlightSegment[] => {
  switch (values.tripType) {
    case "oneway":
      return values.flights.slice(0, 1);
    case "roundtrip":
      return values.flights.slice(0, 2);
    case "multicity":
      return values.flights;
    default:
      return [];
  }
};

const getPassengerTypes = (
  values: TFlightSearchFormData,
  passengers: TFlightSearchFormData["passengers"],
  childrenAges: TFlightSearchFormData["childrenAges"],
): IPassengerTypeQuantity[] => {
  const types: IPassengerTypeQuantity[] = [];

  if (passengers.adults > 0) {
    types.push({
      Code: renderPassengerCode(values.fareType),
      Quantity: passengers.adults,
    });
  }

  const ageFrequency = new Map<number, number>();
  for (const age of childrenAges) {
    if (typeof age !== "number") continue;
    if (age < 2 || age > 11) continue;
    ageFrequency.set(age, (ageFrequency.get(age) ?? 0) + 1);
  }

  for (const [age, quantity] of Array.from(ageFrequency.entries()).sort(
    ([a], [b]) => a - b,
  )) {
    types.push({ Code: `C${String(age).padStart(2, "0")}`, Quantity: quantity });
  }

  if (passengers.infants > 0) {
    types.push({ Code: "INF", Quantity: passengers.infants });
  }

  return types;
};

export const FlightSearchFormatter = (
  values: TFlightSearchFormData,
): IFlightSearchRequestPayload => {
  const journeyFlights = getJourneyFlights(values);
  const legs = journeyFlights
    .filter(isValidFlightSegment)
    .map((segment, index) => createLeg(index + 1, segment, values.cabin));

  return {
    JourneyType: JOURNEY_TYPE_MAP[values.tripType],
    PassengerTypeQuantity: getPassengerTypes(
      values,
      values.passengers,
      values.childrenAges,
    ),
    OriginDestinationInformation: legs,
  };
};




